import React, { useState, useEffect, useRef } from 'react';
import {
  Plus,
  Search,
  MessageSquare,
  Copy,
  Trash2,
  Play,
  Pause,
  Edit,
  Upload,
  FileText,
  Database,
  X,
  CheckCircle,
  AlertCircle,
  Code,
  Globe,
  Link as LinkIcon,
  Lock,
  Key,
  RefreshCw
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { chatbotService } from '../../../services/chatbotService';
import { billingService } from '../../../services/billingService';
import { authService } from '../../../services/authService';
import { externalApiService } from '../../../services/externalApiService';
import ChatbotChannels from './ChatbotChannels';
import { useDateFormat } from '../../../hooks/useDateFormat';

const ManageChatbots = () => {
  const { t } = useTranslation();
  const { formatDate } = useDateFormat();

  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [chatbots, setChatbots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [maxChatbots, setMaxChatbots] = useState(null); // null = unlimited (trial)
  const [createError, setCreateError] = useState(null);

  // Create/Edit modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingBot, setEditingBot] = useState(null);
  const [formData, setFormData] = useState({
    name: '', description: '', primaryColor: '#007bff',
    welcomeMessage: 'Hello! How can I help you?', systemPrompt: ''
  });
  const [saving, setSaving] = useState(false);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const avatarInputRef = useRef(null);

  // Knowledge base modal
  const [showKnowledgeBaseModal, setShowKnowledgeBaseModal] = useState(false);
  const [selectedBot, setSelectedBot] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [documentQuota, setDocumentQuota] = useState(null); // { current, max, unlimited }
  const fileInputRef = useRef(null);
  const pollingRef = useRef(null);

  // URL knowledge source
  const [urlInput, setUrlInput] = useState('');
  const [addingUrl, setAddingUrl] = useState(false);

  // External APIs
  const [externalApis, setExternalApis] = useState([]);
  const [showApiForm, setShowApiForm] = useState(false);
  const [editingApiId, setEditingApiId] = useState(null);
  const [apiFormData, setApiFormData] = useState({
    name: '', description: '', endpointUrl: '', authHeaderName: '', authHeaderValue: ''
  });
  const [apiSaving, setApiSaving] = useState(false);
  const [apiTestResult, setApiTestResult] = useState(null);
  const [apiTesting, setApiTesting] = useState(false);
  const [apiError, setApiError] = useState(null);

  // Embed code modal
  const [showEmbedModal, setShowEmbedModal] = useState(false);
  const [embedBot, setEmbedBot] = useState(null);
  const [copied, setCopied] = useState(false);
  const [copiedKey, setCopiedKey] = useState(false);
  const [generatedApiKey, setGeneratedApiKey] = useState(null);
  const [copiedApiKey, setCopiedApiKey] = useState(false);
  const [apiKeyLoading, setApiKeyLoading] = useState(false);
  const [confirmRevokeKey, setConfirmRevokeKey] = useState(false);

  // Delete confirm
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    fetchChatbots();
    fetchSubscriptionLimit();
  }, []);

  const fetchSubscriptionLimit = async () => {
    try {
      const companyId = authService.getCompanyId();
      if (!companyId) return;
      const sub = await billingService.getChatbotSubscription(companyId);
      setMaxChatbots(sub?.maxChatbots ?? null);
    } catch {
      // If subscription service is down, don't block the UI
    }
  };

  // Auto-poll documents while any are still processing
  useEffect(() => {
    const hasPending = documents.some(
      d => d.status === 'PENDING' || d.status === 'PROCESSING'
    );

    if (hasPending && showKnowledgeBaseModal && selectedBot) {
      if (!pollingRef.current) {
        pollingRef.current = setInterval(async () => {
          try {
            const docs = await chatbotService.getDocuments(selectedBot.id);
            setDocuments(docs || []);
          } catch { /* silent */ }
        }, 3000);
      }
    } else {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }

    return () => {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    };
  }, [documents, showKnowledgeBaseModal, selectedBot]);

  const fetchChatbots = async () => {
    try {
      setLoading(true);
      const data = await chatbotService.getChatbots();
      setChatbots(data || []);
    } catch (err) {
      console.error('Failed to load chatbots:', err);
      setError('Failed to load chatbots');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrUpdate = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) return;
    setSaving(true);
    setCreateError(null);
    try {
      let saved;
      if (editingBot) {
        saved = await chatbotService.updateChatbot(editingBot.id, formData);
      } else {
        saved = await chatbotService.createChatbot(formData);
      }
      if (avatarFile && saved?.id) {
        await chatbotService.uploadAvatar(saved.id, avatarFile);
      }
      setShowCreateModal(false);
      setEditingBot(null);
      resetForm();
      fetchChatbots();
    } catch (err) {
      console.error('Failed to save chatbot:', err);
      setCreateError(err?.message || 'Failed to save chatbot. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (bot) => {
    try {
      await chatbotService.updateChatbot(bot.id, { isActive: !bot.isActive });
      fetchChatbots();
    } catch (err) {
      console.error('Failed to toggle chatbot:', err);
    }
  };

  const handleDelete = async (id) => {
    try {
      await chatbotService.deleteChatbot(id);
      setDeletingId(null);
      fetchChatbots();
    } catch (err) {
      console.error('Failed to delete chatbot:', err);
    }
  };

  const openEditModal = (bot) => {
    setEditingBot(bot);
    setFormData({
      name: bot.name || '',
      description: bot.description || '',
      primaryColor: bot.primaryColor || '#007bff',
      welcomeMessage: bot.welcomeMessage || '',
      systemPrompt: bot.systemPrompt || '',
    });
    setAvatarFile(null);
    setAvatarPreview(bot.botAvatarUrl || null);
    setShowCreateModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '', description: '', primaryColor: '#007bff',
      welcomeMessage: 'Hello! How can I help you?', systemPrompt: ''
    });
    setAvatarFile(null);
    setAvatarPreview(null);
  };

  const handleAvatarFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setAvatarFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setAvatarPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const openKnowledgeBase = async (bot) => {
    setSelectedBot(bot);
    setUrlInput('');
    setExternalApis([]);
    setShowApiForm(false);
    setEditingApiId(null);
    setApiFormData({ name: '', description: '', endpointUrl: '', authHeaderName: '', authHeaderValue: '' });
    setApiTestResult(null);
    setApiError(null);
    setShowKnowledgeBaseModal(true);
    setLoadingDocs(true);
    try {
      const docs = await chatbotService.getDocuments(bot.id);
      setDocuments(docs || []);
    } catch (err) {
      console.error('Failed to load documents:', err);
      setDocuments([]);
    } finally {
      setLoadingDocs(false);
    }
    try {
      const quota = await chatbotService.getDocumentQuota(bot.id);
      setDocumentQuota(quota);
    } catch (err) {
      console.error('Failed to load document quota:', err);
      setDocumentQuota(null);
    }
    loadExternalApis(bot.id);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedBot) return;
    setUploading(true);
    try {
      await chatbotService.uploadDocument(selectedBot.id, file);
      // Refresh docs and quota independently so a docs failure doesn't skip the quota update
      try {
        const docs = await chatbotService.getDocuments(selectedBot.id);
        setDocuments(docs || []);
      } catch (err) {
        console.warn('Failed to refresh documents after upload:', err);
      }
      try {
        const quota = await chatbotService.getDocumentQuota(selectedBot.id);
        setDocumentQuota(quota);
      } catch (err) {
        console.warn('Failed to refresh quota after upload:', err);
      }
    } catch (err) {
      console.error('Failed to upload document:', err);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteDocument = async (docId) => {
    try {
      await chatbotService.deleteDocument(docId);
      setDocuments(prev => prev.filter(d => d.id !== docId));
    } catch (err) {
      console.error('Failed to delete document:', err);
    }
    try {
      const quota = await chatbotService.getDocumentQuota(selectedBot.id);
      setDocumentQuota(quota);
    } catch (err) {
      console.warn('Failed to refresh quota after delete:', err);
    }
  };

  const handleAddUrl = async (e) => {
    e.preventDefault();
    const url = urlInput.trim();
    if (!url || !selectedBot) return;
    setAddingUrl(true);
    try {
      await chatbotService.addUrlSource(selectedBot.id, url);
      setUrlInput('');
      // Refresh docs and quota independently so a docs failure doesn't skip the quota update
      try {
        const docs = await chatbotService.getDocuments(selectedBot.id);
        setDocuments(docs || []);
      } catch (err) {
        console.warn('Failed to refresh documents after adding URL:', err);
      }
      try {
        const quota = await chatbotService.getDocumentQuota(selectedBot.id);
        setDocumentQuota(quota);
      } catch (err) {
        console.warn('Failed to refresh quota after adding URL:', err);
      }
    } catch (err) {
      console.error('Failed to add URL:', err);
    } finally {
      setAddingUrl(false);
    }
  };

  const loadExternalApis = async (chatbotId) => {
    try {
      const apis = await externalApiService.getApis(chatbotId);
      setExternalApis(apis || []);
    } catch (err) {
      console.error('Failed to load external APIs:', err);
    }
  };

  const handleOpenNewApiForm = () => {
    setEditingApiId(null);
    setApiFormData({ name: '', description: '', endpointUrl: '', authHeaderName: '', authHeaderValue: '' });
    setApiTestResult(null);
    setApiError(null);
    setShowApiForm(true);
  };

  const handleOpenEditApiForm = (api) => {
    setEditingApiId(api.id);
    setApiFormData({
      name:            api.name,
      description:     api.description,
      endpointUrl:     api.endpointUrl,
      authHeaderName:  api.authHeaderName || '',
      authHeaderValue: api.authHeaderValue || '',
    });
    setApiTestResult(null);
    setApiError(null);
    setShowApiForm(true);
  };

  const handleCancelApiForm = () => {
    setShowApiForm(false);
    setEditingApiId(null);
    setApiTestResult(null);
    setApiError(null);
  };

  const handleTestAndSaveApi = async () => {
    if (!apiFormData.name.trim()) { setApiError('Name is required'); return; }
    if (!apiFormData.description.trim()) { setApiError('Description is required'); return; }
    if (!apiFormData.endpointUrl.trim()) { setApiError('Endpoint URL is required'); return; }
    setApiError(null);
    setApiSaving(true);
    try {
      let saved;
      if (editingApiId) {
        saved = await externalApiService.updateApi(selectedBot.id, editingApiId, apiFormData);
      } else {
        saved = await externalApiService.createApi(selectedBot.id, apiFormData);
      }
      setApiTesting(true);
      try {
        const result = await externalApiService.testApi(selectedBot.id, saved.id);
        setApiTestResult(result);
        if (result.reachable) {
          await loadExternalApis(selectedBot.id);
          setShowApiForm(false);
          setEditingApiId(null);
        }
      } catch {
        setApiTestResult({ reachable: false, error: 'Test request failed' });
      } finally {
        setApiTesting(false);
      }
    } catch (err) {
      setApiError(err?.message || 'Failed to save API configuration');
    } finally {
      setApiSaving(false);
    }
  };

  const handleToggleApi = async (api) => {
    try {
      const updated = await externalApiService.toggleApi(selectedBot.id, api.id);
      setExternalApis(prev => prev.map(a => a.id === api.id ? updated : a));
    } catch (err) {
      console.error('Failed to toggle API:', err);
    }
  };

  const handleDeleteApi = async (apiId) => {
    if (!window.confirm('Delete this external API configuration?')) return;
    try {
      await externalApiService.deleteApi(selectedBot.id, apiId);
      setExternalApis(prev => prev.filter(a => a.id !== apiId));
      if (editingApiId === apiId) handleCancelApiForm();
    } catch (err) {
      console.error('Failed to delete API:', err);
    }
  };

  const handleCopyEmbed = (bot) => {
    setEmbedBot(bot);
    setShowEmbedModal(true);
    setCopied(false);
    setCopiedKey(false);
    setGeneratedApiKey(null);
    setConfirmRevokeKey(false);
  };

  const handleGenerateApiKey = async () => {
    setApiKeyLoading(true);
    try {
      const res = await chatbotService.generateApiKey(embedBot.id);
      const key = res.data?.apiKey || res.apiKey;
      setGeneratedApiKey(key);
      setEmbedBot(prev => ({ ...prev, hasApiKey: true }));
      setChatbots(prev => prev.map(b => b.id === embedBot.id ? { ...b, hasApiKey: true } : b));
      setConfirmRevokeKey(false);
    } catch (e) {
      console.error('Failed to generate API key', e);
    } finally {
      setApiKeyLoading(false);
    }
  };

  const handleRevokeApiKey = async () => {
    setApiKeyLoading(true);
    try {
      await chatbotService.revokeApiKey(embedBot.id);
      setGeneratedApiKey(null);
      setEmbedBot(prev => ({ ...prev, hasApiKey: false }));
      setChatbots(prev => prev.map(b => b.id === embedBot.id ? { ...b, hasApiKey: false } : b));
      setConfirmRevokeKey(false);
    } catch (e) {
      console.error('Failed to revoke API key', e);
    } finally {
      setApiKeyLoading(false);
    }
  };

  const copyApiKey = () => {
    if (generatedApiKey) {
      navigator.clipboard.writeText(generatedApiKey);
      setCopiedApiKey(true);
      setTimeout(() => setCopiedApiKey(false), 2000);
    }
  };

  const copyToClipboard = () => {
    if (embedBot?.embedCode) {
      navigator.clipboard.writeText(embedBot.embedCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const copyWidgetKey = () => {
    if (embedBot?.widgetKey) {
      navigator.clipboard.writeText(embedBot.widgetKey);
      setCopiedKey(true);
      setTimeout(() => setCopiedKey(false), 2000);
    }
  };

  const getStatusBadge = (isActive) => {
    if (isActive) return 'bg-green-100 text-green-800';
    return 'bg-yellow-100 text-yellow-800';
  };

  const filteredBots = chatbots.filter(bot => {
    const matchesSearch = (bot.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (bot.description || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' ||
                         (filterStatus === 'active' && bot.isActive) ||
                         (filterStatus === 'paused' && !bot.isActive);
    return matchesSearch && matchesFilter;
  });

  const activeBots = chatbots.filter(b => b.isActive).length;
  const pausedBots = chatbots.filter(b => !b.isActive).length;
  const atLimit = maxChatbots !== null && chatbots.length >= maxChatbots;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-20">
        <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('common.error')}</h2>
        <button onClick={fetchChatbots} className="text-primary-600 hover:underline">Try again</button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('chatbot.manage')}</h1>
          <p className="text-gray-600 mt-1">{t('chatbot.manageSubtitle')}</p>
        </div>
        <button
          onClick={() => { if (!atLimit) { resetForm(); setEditingBot(null); setCreateError(null); setShowCreateModal(true); } }}
          className={`btn ${atLimit ? 'btn-outline opacity-60 cursor-not-allowed' : 'btn-primary'}`}
          disabled={atLimit}
          title={atLimit ? `Plan limit reached (${maxChatbots} chatbot${maxChatbots === 1 ? '' : 's'} max). Upgrade to add more.` : undefined}
        >
          {atLimit ? <Lock className="w-5 h-5 mr-2" /> : <Plus className="w-5 h-5 mr-2" />}
          {t('chatbot.cb_addChatbot')}
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <div className={`bg-white rounded-lg shadow-sm border p-4 ${atLimit ? 'border-orange-300 bg-orange-50' : 'border-gray-200'}`}>
          <div className={`text-2xl font-bold ${atLimit ? 'text-orange-600' : 'text-gray-900'}`}>
            {chatbots.length}{maxChatbots !== null ? ` / ${maxChatbots}` : ''}
          </div>
          <div className="text-sm text-gray-600">
            {t('chatbot.totalChatbots')}
            {atLimit && <span className="ml-1 text-orange-600 font-medium">{t('chatbot.cb_limitReached')}</span>}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="text-2xl font-bold text-green-600">{activeBots}</div>
          <div className="text-sm text-gray-600">{t('common.active')}</div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="text-2xl font-bold text-yellow-600">{pausedBots}</div>
          <div className="text-sm text-gray-600">{t('chatbot.cb_paused')}</div>
        </div>
      </div>

      {/* Limit banner */}
      {atLimit && (
        <div className="flex items-start gap-3 bg-orange-50 border border-orange-200 rounded-lg p-4">
          <Lock className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-orange-800">{t('chatbot.cb_chatbotLimitReached')}</p>
            <p className="text-sm text-orange-700 mt-0.5">
              {t('chatbot.cb_chatbotLimitDesc', { max: maxChatbots })}
            </p>
          </div>
        </div>
      )}

      {/* Search & Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[300px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search chatbots..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg"
          >
            <option value="all">{t('common.allStatus')}</option>
            <option value="active">{t('common.active')}</option>
            <option value="paused">{t('chatbot.cb_paused')}</option>
          </select>
        </div>
      </div>

      {/* Chatbots List */}
      {filteredBots.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {chatbots.length === 0 ? t('chatbot.cb_noChatbots') : 'No chatbots match your search'}
          </h3>
          <p className="text-gray-500 mb-4">
            {chatbots.length === 0 ? t('chatbot.cb_createFirst') : 'Try adjusting your search or filters.'}
          </p>
          {chatbots.length === 0 && !atLimit && (
            <button
              onClick={() => { resetForm(); setEditingBot(null); setCreateError(null); setShowCreateModal(true); }}
              className="btn btn-primary"
            >
              <Plus className="w-5 h-5 mr-2" />
              {t('chatbot.cb_addChatbot')}
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('common.name')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('chatbot.cb_status')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('chatbot.cb_conversations')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('chatbot.cb_messages')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('chatbot.cb_knowledgeBase')}</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredBots.map((bot) => (
                  <tr key={bot.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        {bot.botAvatarUrl ? (
                          <img
                            src={bot.botAvatarUrl}
                            alt={bot.name}
                            className="w-10 h-10 rounded-full object-cover flex-shrink-0 border border-gray-200"
                          />
                        ) : (
                          <div
                            className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
                            style={{ backgroundColor: bot.primaryColor || '#007bff' }}
                          >
                            {(bot.name || '?').charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div>
                          <div className="font-medium text-gray-900">{bot.name}</div>
                          <div className="text-xs text-gray-500 line-clamp-1">{bot.description || 'No description'}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(bot.isActive)}`}>
                        {bot.isActive ? t('common.active') : t('chatbot.cb_paused')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                      {(bot.totalConversations || 0).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                      {(bot.totalMessages || 0).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => openKnowledgeBase(bot)}
                        className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
                      >
                        <Database className="w-4 h-4" />
                        {t('chatbot.cb_configure')}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => openEditModal(bot)}
                          className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg"
                          title={t('common.edit')}
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleToggleActive(bot)}
                          className={`p-2 rounded-lg ${bot.isActive
                            ? 'text-gray-400 hover:text-yellow-600 hover:bg-yellow-50'
                            : 'text-gray-400 hover:text-green-600 hover:bg-green-50'
                          }`}
                          title={bot.isActive ? t('chatbot.cb_pause') : t('chatbot.cb_activate')}
                        >
                          {bot.isActive ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => handleCopyEmbed(bot)}
                          className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg"
                          title={t('chatbot.cb_embedCode')}
                        >
                          <Code className="w-4 h-4" />
                        </button>
                        {deletingId === bot.id ? (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleDelete(bot.id)}
                              className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                            >
                              Confirm
                            </button>
                            <button
                              onClick={() => setDeletingId(null)}
                              className="px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                            >
                              {t('common.cancel')}
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setDeletingId(bot.id)}
                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                            title={t('common.delete')}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create/Edit Chatbot Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">
                {editingBot ? t('chatbot.cb_editChatbot') : t('chatbot.cb_addChatbot')}
              </h2>
              <button onClick={() => setShowCreateModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-6 h-6 text-gray-500" />
              </button>
            </div>
            <form onSubmit={handleCreateOrUpdate}>
              <div className="p-6 space-y-4">
                {createError && (
                  <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg p-3">
                    <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-700">{createError}</p>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('chatbot.cb_botName')} *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Customer Support Bot"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="What does this chatbot do?"
                    rows={2}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('chatbot.cb_welcomeMessage')}</label>
                  <input
                    type="text"
                    value={formData.welcomeMessage}
                    onChange={(e) => setFormData({ ...formData, welcomeMessage: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('chatbot.cb_systemPrompt')}</label>
                  <textarea
                    value={formData.systemPrompt}
                    onChange={(e) => setFormData({ ...formData, systemPrompt: e.target.value })}
                    placeholder="Instructions for the AI assistant..."
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('chatbot.cb_primaryColor')}</label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={formData.primaryColor}
                      onChange={(e) => setFormData({ ...formData, primaryColor: e.target.value })}
                      className="w-10 h-10 border border-gray-300 rounded cursor-pointer"
                    />
                    <input
                      type="text"
                      value={formData.primaryColor}
                      onChange={(e) => setFormData({ ...formData, primaryColor: e.target.value })}
                      className="w-28 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Avatar</label>
                  <div className="flex items-center gap-4">
                    {/* Preview circle */}
                    <div
                      className="w-16 h-16 rounded-full flex-shrink-0 overflow-hidden flex items-center justify-center cursor-pointer border-2 border-dashed border-gray-300 hover:border-primary-400 transition-colors"
                      onClick={() => avatarInputRef.current?.click()}
                      title="Click to upload avatar"
                    >
                      {avatarPreview ? (
                        <img src={avatarPreview} alt="Avatar" className="w-full h-full object-cover" />
                      ) : (
                        <span
                          className="text-2xl font-bold text-white w-full h-full flex items-center justify-center rounded-full"
                          style={{ backgroundColor: formData.primaryColor || '#007bff' }}
                        >
                          {(formData.name || '?').charAt(0).toUpperCase() || '?'}
                        </span>
                      )}
                    </div>
                    <div className="flex-1">
                      <button
                        type="button"
                        onClick={() => avatarInputRef.current?.click()}
                        className="btn btn-outline text-sm"
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        {avatarPreview ? 'Change Avatar' : 'Upload Avatar'}
                      </button>
                      {avatarPreview && (
                        <button
                          type="button"
                          onClick={() => { setAvatarFile(null); setAvatarPreview(null); }}
                          className="ml-2 text-sm text-red-500 hover:text-red-700"
                        >
                          Remove
                        </button>
                      )}
                      <p className="text-xs text-gray-400 mt-1">PNG, JPG or GIF · Max 2MB</p>
                    </div>
                  </div>
                  <input
                    ref={avatarInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/gif,image/webp"
                    onChange={handleAvatarFileChange}
                    className="hidden"
                  />
                </div>
              </div>
              <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
                <button type="button" onClick={() => setShowCreateModal(false)} className="btn btn-outline">
                  {t('common.cancel')}
                </button>
                <button type="submit" disabled={saving || !formData.name.trim()} className="btn btn-primary">
                  {saving ? t('common.loading') : editingBot ? t('common.save') : t('chatbot.cb_addChatbot')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Knowledge Base Configuration Modal */}
      {showKnowledgeBaseModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{t('chatbot.cb_knowledgeBase')}</h2>
                <p className="text-sm text-gray-600 mt-1">{selectedBot?.name}</p>
              </div>
              <button onClick={() => { setShowKnowledgeBaseModal(false); setDocumentQuota(null); }} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-6 h-6 text-gray-500" />
              </button>
            </div>

            {(() => {
              const atDocLimit = !!(documentQuota && !documentQuota.unlimited && documentQuota.current >= documentQuota.max);
              return (
            <div className="p-6 space-y-6">
              {/* Document quota bar */}
              {documentQuota && !documentQuota.unlimited && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 flex items-center justify-between">
                  <span className="text-sm text-blue-800 font-medium">
                    Documents used: {documentQuota.current} / {documentQuota.max}
                  </span>
                  <div className="w-32 bg-blue-200 rounded-full h-2 ml-4">
                    <div
                      className={`h-2 rounded-full transition-all ${
                        documentQuota.current >= documentQuota.max ? 'bg-red-500' : 'bg-blue-600'
                      }`}
                      style={{ width: `${Math.min((documentQuota.current / documentQuota.max) * 100, 100)}%` }}
                    />
                  </div>
                </div>
              )}
              {documentQuota && !documentQuota.unlimited && documentQuota.current >= documentQuota.max && (
                <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3">
                  <p className="text-sm text-red-700 font-medium">
                    Document limit reached. Delete an existing document or upgrade your plan to add more.
                  </p>
                </div>
              )}
              {/* File Upload Section */}
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Upload className="w-5 h-5 text-primary-600" />
                  {t('chatbot.cb_uploadDocument')}
                </h3>
                <div
                  onClick={() => !atDocLimit && fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                    atDocLimit
                      ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-50'
                      : 'border-gray-300 hover:border-primary-400 cursor-pointer'
                  }`}
                >
                  <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-sm font-medium text-gray-900 mb-2">
                    {uploading ? t('common.loading') : atDocLimit ? 'Document limit reached' : 'Drop files here or click to browse'}
                  </p>
                  <p className="text-xs text-gray-500">
                    {atDocLimit
                      ? 'Delete an existing document or upgrade your plan to add more.'
                      : 'Supported: PDF, DOCX, TXT, CSV (Max 10MB per file)'}
                  </p>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.docx,.txt,.csv,.json"
                  onChange={handleFileUpload}
                  disabled={atDocLimit}
                  className="hidden"
                />
              </div>

              {/* Divider */}
              <div className="flex items-center gap-3">
                <div className="flex-1 border-t border-gray-200" />
                <span className="text-xs text-gray-400 font-medium">OR</span>
                <div className="flex-1 border-t border-gray-200" />
              </div>

              {/* Website URL Section */}
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Globe className="w-5 h-5 text-primary-600" />
                  Add Website URL
                </h3>
                <p className="text-sm text-gray-500 mb-3">
                  The bot will crawl the page and use its content as a knowledge source.
                </p>
                <form onSubmit={handleAddUrl} className="flex gap-2">
                  <input
                    type="url"
                    value={urlInput}
                    onChange={(e) => setUrlInput(e.target.value)}
                    placeholder="https://example.com/about"
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
                  />
                  <button
                    type="submit"
                    disabled={addingUrl || !urlInput.trim() || atDocLimit}
                    className="btn btn-primary flex-shrink-0"
                  >
                    {addingUrl ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <><LinkIcon className="w-4 h-4 mr-2" />{t('chatbot.cb_addUrl')}</>
                    )}
                  </button>
                </form>
              </div>

              {/* Knowledge Sources List */}
              {loadingDocs ? (
                <div className="text-center py-4">
                  <div className="w-6 h-6 border-2 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
                </div>
              ) : (() => {
                const urlDocs  = documents.filter(d => d.fileType === 'text/html' || (d.fileName || '').startsWith('http'));
                const fileDocs = documents.filter(d => !(d.fileType === 'text/html' || (d.fileName || '').startsWith('http')));

                const statusBadge = (status) => {
                  if (status === 'PROCESSED' || status === 'COMPLETED')
                    return { cls: 'bg-green-100 text-green-800', label: 'Ready', spin: false };
                  if (status === 'PROCESSING')
                    return { cls: 'bg-blue-100 text-blue-800', label: 'Indexing…', spin: true };
                  if (status === 'FAILED')
                    return { cls: 'bg-red-100 text-red-800', label: 'Failed', spin: false };
                  // PENDING or unknown
                  return { cls: 'bg-yellow-100 text-yellow-800', label: 'Processing…', spin: true };
                };

                const DocRow = ({ doc, icon, subtitle }) => {
                  const badge = statusBadge(doc.status);
                  return (
                  <div key={doc.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-center gap-3 min-w-0">
                      {icon}
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate max-w-sm" title={doc.fileName || doc.name}>
                          {doc.fileName || doc.name}
                        </div>
                        <div className="text-xs text-gray-500">
                          {subtitle}
                          {doc.chunkCount > 0 ? ` • ${doc.chunkCount} chunks` : ''}
                          {doc.createdAt ? ` • ${formatDate(doc.createdAt)}` : ''}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className={`px-2 py-1 rounded text-xs font-medium flex items-center gap-1 ${badge.cls}`}>
                        {badge.spin && (
                          <span className="inline-block w-2.5 h-2.5 border border-current border-t-transparent rounded-full animate-spin" />
                        )}
                        {badge.label}
                      </span>
                      <button onClick={() => handleDeleteDocument(doc.id)} className="p-1 hover:bg-gray-200 rounded">
                        <Trash2 className="w-4 h-4 text-gray-400 hover:text-red-600" />
                      </button>
                    </div>
                  </div>
                  );
                };

                return (
                  <div className="space-y-5">
                    {/* Website URLs */}
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Globe className="w-4 h-4 text-blue-500" />
                        <span className="text-sm font-semibold text-gray-700">Website URLs</span>
                        <span className="ml-1 px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                          {urlDocs.length}
                        </span>
                      </div>
                      {urlDocs.length === 0 ? (
                        <p className="text-xs text-gray-400 pl-6">No URLs added yet.</p>
                      ) : (
                        <div className="space-y-2">
                          {urlDocs.map(doc => (
                            <DocRow
                              key={doc.id}
                              doc={doc}
                              icon={<Globe className="w-5 h-5 text-blue-500 flex-shrink-0" />}
                              subtitle="Website"
                            />
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Uploaded Documents */}
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <FileText className="w-4 h-4 text-primary-600" />
                        <span className="text-sm font-semibold text-gray-700">{t('chatbot.cb_documents')}</span>
                        <span className="ml-1 px-1.5 py-0.5 bg-primary-100 text-primary-700 rounded text-xs font-medium">
                          {fileDocs.length}
                        </span>
                      </div>
                      {fileDocs.length === 0 ? (
                        <p className="text-xs text-gray-400 pl-6">{t('chatbot.cb_noDocuments')}</p>
                      ) : (
                        <div className="space-y-2">
                          {fileDocs.map(doc => (
                            <DocRow
                              key={doc.id}
                              doc={doc}
                              icon={<FileText className="w-5 h-5 text-primary-600 flex-shrink-0" />}
                              subtitle={doc.fileSize ? `${(doc.fileSize / 1024).toFixed(0)} KB` : ''}
                            />
                          ))}
                        </div>
                      )}
                    </div>

                    {documents.length === 0 && (
                      <p className="text-center text-sm text-gray-500">No knowledge sources added yet.</p>
                    )}
                  </div>
                );
              })()}

              {/* ── External APIs Section ── */}
              <div className="mt-8 border-t pt-6">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-semibold text-gray-800 flex items-center gap-2">
                    <Globe className="w-4 h-4 text-indigo-500" />
                    External APIs
                  </h4>
                  {!showApiForm && externalApis.length < 5 && (
                    <button
                      onClick={handleOpenNewApiForm}
                      className="flex items-center gap-1 text-sm bg-indigo-600 text-white px-3 py-1.5 rounded-lg hover:bg-indigo-700 transition"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      Add API
                    </button>
                  )}
                </div>

                {externalApis.length === 0 && !showApiForm && (
                  <p className="text-sm text-gray-400 text-center py-4">
                    No external APIs configured. Add one to let the chatbot query live data.
                  </p>
                )}

                {externalApis.map(api => (
                  <div key={api.id} className={`mb-3 rounded-xl border p-4 ${api.isEnabled ? 'border-indigo-200 bg-indigo-50' : 'border-gray-200 bg-gray-50'}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-gray-800 text-sm">{api.name}</span>
                          <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${api.isEnabled ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-500'}`}>
                            {api.isEnabled ? 'Enabled' : 'Disabled'}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mb-1">{api.description}</p>
                        <p className="text-xs text-gray-400 font-mono truncate" title={api.endpointUrl}>
                          {api.endpointUrl}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          onClick={() => handleToggleApi(api)}
                          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${api.isEnabled ? 'bg-indigo-500' : 'bg-gray-300'}`}
                          title={api.isEnabled ? 'Disable' : 'Enable'}
                        >
                          <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${api.isEnabled ? 'translate-x-4' : 'translate-x-1'}`} />
                        </button>
                        <button
                          onClick={() => handleOpenEditApiForm(api)}
                          className="p-1.5 text-gray-400 hover:text-indigo-600 rounded"
                          title="Edit"
                        >
                          <Edit className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => handleDeleteApi(api.id)}
                          className="p-1.5 text-gray-400 hover:text-red-500 rounded"
                          title="Delete"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}

                {showApiForm && (
                  <div className="rounded-xl border border-indigo-200 bg-white p-4 mt-2">
                    <h5 className="font-semibold text-gray-700 text-sm mb-3">
                      {editingApiId ? 'Edit API' : 'New External API'}
                    </h5>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Name *</label>
                        <input
                          type="text"
                          value={apiFormData.name}
                          onChange={e => setApiFormData(f => ({ ...f, name: e.target.value }))}
                          placeholder="Product Search"
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Description * <span className="font-normal text-gray-400">(tells the AI when to use this API)</span>
                        </label>
                        <textarea
                          value={apiFormData.description}
                          onChange={e => setApiFormData(f => ({ ...f, description: e.target.value }))}
                          placeholder="Use when visitor asks about products, prices or stock availability"
                          rows={2}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 resize-none"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Endpoint URL * <span className="font-normal text-gray-400">(use {'{query}'} placeholder for search, or omit for full list)</span>
                        </label>
                        <input
                          type="text"
                          value={apiFormData.endpointUrl}
                          onChange={e => setApiFormData(f => ({ ...f, endpointUrl: e.target.value }))}
                          placeholder="https://myshop.com/api/products?q={query}&limit=5 or https://myshop.com/api/products"
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Auth Header Name <span className="font-normal text-gray-400">(optional)</span>
                          </label>
                          <input
                            type="text"
                            value={apiFormData.authHeaderName}
                            onChange={e => setApiFormData(f => ({ ...f, authHeaderName: e.target.value }))}
                            placeholder="X-Shopify-Access-Token"
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Auth Header Value <span className="font-normal text-gray-400">(optional)</span>
                          </label>
                          <input
                            type="password"
                            value={apiFormData.authHeaderValue}
                            onChange={e => setApiFormData(f => ({ ...f, authHeaderValue: e.target.value }))}
                            placeholder="shpat_xxxxxxxxxxxx"
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                          />
                        </div>
                      </div>
                      {apiTestResult && (
                        <div className={`rounded-lg p-3 text-xs ${apiTestResult.reachable ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-600'}`}>
                          {apiTestResult.reachable ? (
                            <>
                              <div className="font-semibold mb-1">✓ API reachable (HTTP {apiTestResult.statusCode})</div>
                              {apiTestResult.preview && (
                                <div className="font-mono break-all opacity-80">{apiTestResult.preview}</div>
                              )}
                            </>
                          ) : (
                            <div>✗ {apiTestResult.error || 'Could not reach the API'}</div>
                          )}
                        </div>
                      )}
                      {apiError && (
                        <div className="rounded-lg p-3 text-xs bg-red-50 border border-red-200 text-red-600">
                          {apiError}
                        </div>
                      )}
                      <div className="flex gap-2 pt-1">
                        <button
                          onClick={handleCancelApiForm}
                          className="flex-1 border border-gray-300 text-gray-600 text-sm py-2 rounded-lg hover:bg-gray-50 transition"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleTestAndSaveApi}
                          disabled={apiSaving || apiTesting}
                          className="flex-1 bg-indigo-600 text-white text-sm py-2 rounded-lg hover:bg-indigo-700 disabled:opacity-60 transition"
                        >
                          {apiTesting ? 'Testing…' : apiSaving ? 'Saving…' : 'Test & Save'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {externalApis.length >= 5 && !showApiForm && (
                  <p className="text-xs text-gray-400 text-center mt-2">Maximum of 5 external APIs reached.</p>
                )}
              </div>

              {/* ── WhatsApp Channels Section ── */}
              <ChatbotChannels chatbotId={selectedBot?.id} />
            </div>
              );
            })()}

            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
              <button onClick={() => { setShowKnowledgeBaseModal(false); setDocumentQuota(null); }} className="btn btn-outline">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Embed Code Modal */}
      {showEmbedModal && embedBot && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">{t('chatbot.cb_embedCode')}</h2>
              <button onClick={() => setShowEmbedModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-6 h-6 text-gray-500" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-600">
                Add this code to your website to deploy <strong>{embedBot.name}</strong>:
              </p>

              {/* Embed code block with copy button */}
              <div className="relative group">
                <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-sm overflow-x-auto whitespace-pre-wrap pr-12">
                  {embedBot.embedCode || 'No embed code generated yet.'}
                </div>
                <button
                  onClick={copyToClipboard}
                  title={t('chatbot.cb_copyEmbedCode')}
                  className="absolute top-2 right-2 p-1.5 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded transition-colors"
                >
                  {copied
                    ? <CheckCircle className="w-4 h-4 text-green-400" />
                    : <Copy className="w-4 h-4" />
                  }
                </button>
              </div>

              {/* Widget key row */}
              <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
                <div className="min-w-0">
                  <div className="text-xs font-medium text-gray-500 mb-0.5">{t('chatbot.cb_widgetKey')}</div>
                  <code className="text-sm text-gray-800 font-mono break-all">{embedBot.widgetKey}</code>
                </div>
                <button
                  onClick={copyWidgetKey}
                  title="Copy widget key"
                  className="ml-3 flex-shrink-0 p-1.5 hover:bg-gray-200 rounded transition-colors text-gray-500"
                >
                  {copiedKey
                    ? <CheckCircle className="w-4 h-4 text-green-500" />
                    : <Copy className="w-4 h-4" />
                  }
                </button>
              </div>

              {/* API Key section */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="flex items-center justify-between bg-gray-50 px-4 py-2.5 border-b border-gray-200">
                  <div className="flex items-center gap-2">
                    <Key className="w-4 h-4 text-gray-500" />
                    <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">{t('chatbot.cb_apiKey')}</span>
                  </div>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${embedBot.hasApiKey ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {embedBot.hasApiKey ? t('chatbot.cb_apiKeyActive') : t('chatbot.cb_apiKeyNone')}
                  </span>
                </div>
                <div className="p-4 space-y-3">
                  <p className="text-xs text-gray-500">{t('chatbot.cb_apiKeyDesc')}</p>

                  {generatedApiKey && (
                    <div className="space-y-2">
                      <div className="flex items-start gap-2 p-2.5 bg-amber-50 border border-amber-200 rounded-lg">
                        <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                        <span className="text-xs text-amber-700">{t('chatbot.cb_apiKeyOnce')}</span>
                      </div>
                      <div className="flex items-center justify-between bg-gray-900 rounded-lg px-3 py-2.5">
                        <code className="text-green-400 font-mono text-xs break-all mr-2">{generatedApiKey}</code>
                        <button onClick={copyApiKey} className="flex-shrink-0 p-1 hover:bg-gray-700 rounded transition-colors text-gray-300">
                          {copiedApiKey ? <CheckCircle className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  )}

                  {confirmRevokeKey && (
                    <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <span className="text-xs text-red-700 flex-1">{t('chatbot.cb_apiKeyRevokeConfirm')}</span>
                      <button onClick={handleRevokeApiKey} disabled={apiKeyLoading} className="text-xs font-medium text-red-600 hover:text-red-700 px-2 py-1 rounded border border-red-300 hover:bg-red-100 disabled:opacity-50">
                        {t('chatbot.cb_apiKeyRevokeYes')}
                      </button>
                      <button onClick={() => setConfirmRevokeKey(false)} className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1">
                        {t('common.cancel')}
                      </button>
                    </div>
                  )}

                  <div className="flex gap-2 flex-wrap">
                    <button
                      onClick={handleGenerateApiKey}
                      disabled={apiKeyLoading}
                      className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                    >
                      <RefreshCw className={`w-3.5 h-3.5 ${apiKeyLoading ? 'animate-spin' : ''}`} />
                      {embedBot.hasApiKey ? t('chatbot.cb_apiKeyRegenerate') : t('chatbot.cb_apiKeyGenerate')}
                    </button>
                    {embedBot.hasApiKey && !confirmRevokeKey && (
                      <button
                        onClick={() => setConfirmRevokeKey(true)}
                        className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 text-red-600 border border-red-300 rounded-lg hover:bg-red-50"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        {t('chatbot.cb_apiKeyRevoke')}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
              <button onClick={() => setShowEmbedModal(false)} className="btn btn-outline">Close</button>
              <button onClick={copyToClipboard} className="btn btn-primary">
                {copied ? (
                  <><CheckCircle className="w-4 h-4 mr-2" />{t('chatbot.cb_copied')}</>
                ) : (
                  <><Copy className="w-4 h-4 mr-2" />{t('chatbot.cb_copyEmbedCode')}</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageChatbots;
